import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { toast } from 'sonner';
import { Users, Clock, UserCircle, LogOut, Globe, Lightbulb, ClipboardList, Zap, Plus, Search, TrendingUp, Calendar, Layers } from 'lucide-react';
import { trackPageView } from '@/utils/analytics';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Social = () => {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [page, setPage] = useState(0);
  const [sortBy, setSortBy] = useState('date');
  const [activeTab, setActiveTab] = useState('newest');
  const [searchQuery, setSearchQuery] = useState('');
  const [showMobileSearch, setShowMobileSearch] = useState(false);
  const [filterTypes, setFilterTypes] = useState({
    idea_pool: true,
    speed_run_quiz: true,
    questionnaire: true
  });
  const [isMobile, setIsMobile] = useState(false);
  const navigate = useNavigate();
  const ITEMS_PER_PAGE = 20;

  useEffect(() => {
    // Detect mobile - portrait orientation (height > width)
    const checkMobile = () => {
      setIsMobile(window.innerHeight > window.innerWidth);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    trackPageView('social');
    // Reset and load initial data when sort changes
    setSessions([]);
    setPage(0);
    setHasMore(true);
    fetchPublicSessions(0, true);
  }, [sortBy]);

  useEffect(() => {
    // Setup infinite scroll listener
    const handleScroll = () => {
      // Check if user is near bottom of page (within 300px)
      const scrollHeight = document.documentElement.scrollHeight;
      const scrollTop = document.documentElement.scrollTop;
      const clientHeight = document.documentElement.clientHeight;
      
      if (scrollHeight - scrollTop - clientHeight < 300 && !loading && !loadingMore && hasMore) {
        loadMore();
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [loading, loadingMore, hasMore, page, sortBy]);

  const fetchPublicSessions = async (pageNum = 0, isInitial = false) => {
    if (isInitial) {
      setLoading(true);
    } else {
      setLoadingMore(true);
    }

    try {
      const token = localStorage.getItem('token');
      const skip = pageNum * ITEMS_PER_PAGE;
      const response = await axios.get(
        `${API}/sessions/public?sort_by=${sortBy}&limit=${ITEMS_PER_PAGE}&skip=${skip}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      const newSessions = response.data;
      
      if (isInitial) {
        setSessions(newSessions);
      } else {
        setSessions(prev => [...prev, ...newSessions]);
      }
      
      // If we received less than ITEMS_PER_PAGE, we've reached the end
      if (newSessions.length < ITEMS_PER_PAGE) {
        setHasMore(false);
      }
    } catch (error) {
      toast.error('Failed to load sessions');
      console.error('Error loading sessions:', error);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  const loadMore = () => {
    const nextPage = page + 1;
    setPage(nextPage);
    fetchPublicSessions(nextPage, false);
  };

  const handleLogout = () => {
    localStorage.clear();
    navigate('/login');
  };

  const handleSessionClick = (session) => {
    // Store referrer for back navigation
    sessionStorage.setItem('referrer', '/social');
    
    if (session.session_type === 'speed_run_quiz') {
      navigate(`/speed-run/${session.id}/summary`);
    } else if (session.session_type === 'questionnaire') {
      navigate(`/facilitator/${session.id}`);
    } else if (session.session_type === 'idea_pool') {
      navigate(`/session/${session.id}/facilitator`);
    }
  };

  const getCountryFlag = (countryCode) => {
    if (!countryCode || countryCode.length !== 2) {
      return '🌐';
    }
    const codePoints = countryCode
      .toUpperCase()
      .split('')
      .map(char => 127397 + char.charCodeAt());
    return String.fromCodePoint(...codePoints);
  };

  const getSessionTypeIcon = (type) => {
    switch (type) {
      case 'idea_pool':
        return <Lightbulb className="h-4 w-4" />;
      case 'questionnaire':
        return <ClipboardList className="h-4 w-4" />;
      case 'speed_run_quiz':
        return <Zap className="h-4 w-4" />;
      default:
        return null;
    }
  };

  const getSessionTypeColor = (type) => {
    switch (type) {
      case 'idea_pool':
        return 'from-purple-500 to-pink-500';
      case 'questionnaire':
        return 'from-blue-500 to-cyan-500';
      case 'speed_run_quiz':
        return 'from-orange-500 to-red-500';
      default:
        return 'from-gray-500 to-gray-600';
    }
  };

  const getSessionTypeName = (type) => {
    switch (type) {
      case 'idea_pool':
        return 'Idea Pool';
      case 'questionnaire':
        return 'Questionnaire';
      case 'speed_run_quiz':
        return 'Speed Quiz';
      default:
        return type;
    }
  };

  const filteredSessions = sessions.filter(session => {
    const matchesFilter = filterTypes[session.session_type];
    const matchesSearch = !searchQuery || 
      session.topic?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      session.hashtags?.some(tag => tag.toLowerCase().includes(searchQuery.toLowerCase())) ||
      session.country_code?.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesFilter && matchesSearch;
  });

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    if (tab === 'trending' || tab === 'top') {
      setSortBy('participants');
    } else {
      setSortBy('date');
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Desktop Layout */}
      {!isMobile ? (
        <>
          {/* Top Header */}
          <div className="bg-white border-b sticky top-0 z-50">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="flex items-center justify-between h-16">
                {/* Logo */}
                <div className="flex items-center gap-2">
                  <img alt="Pollar Logo" className="w-6 h-6" src="/assets/logo/pollar.svg" />
                  <h1 className="text-2xl font-bold text-purple-600">Pollar</h1>
                </div>

                {/* Search Bar */}
                <div className="flex-1 max-w-2xl mx-8">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <Input
                      type="text"
                      placeholder="Search hashtags, topics, or countries..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="pl-10 w-full bg-gray-100 border-0"
                    />
                  </div>
                </div>

                {/* Right Actions */}
                <div className="flex items-center gap-4">
                  <Button
                    onClick={() => navigate('/session/setup')}
                    className="bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white"
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    New Session
                  </Button>

                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" className="rounded-full w-10 h-10 p-0">
                        <UserCircle className="h-6 w-6" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-48">
                      <DropdownMenuItem onClick={() => navigate('/profile')}>
                        <UserCircle className="h-4 w-4 mr-2" />
                        Profile
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem onClick={handleLogout}>
                        <LogOut className="h-4 w-4 mr-2" />
                        Logout
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </div>
            </div>
          </div>

          {/* Main Content */}
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            <div className="flex gap-8">
              {/* Left Sidebar */}
              <div className="w-64 flex-shrink-0">
                <div className="sticky top-24">
                  {/* Menu */}
                  <div className="mb-8">
                    <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-4">Menu</h3>
                    <div className="space-y-2">
                      <Button
                        variant="ghost"
                        className="w-full justify-start text-purple-600 bg-purple-50"
                        onClick={() => navigate('/social')}
                      >
                        <Globe className="h-4 w-4 mr-2" />
                        Explore
                      </Button>
                      <Button
                        variant="ghost"
                        className="w-full justify-start"
                        onClick={() => navigate('/dashboard')}
                      >
                        <Layers className="h-4 w-4 mr-2" />
                        My Sessions
                      </Button>
                    </div>
                  </div>

                  {/* Filter by Type */}
                  <div>
                    <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-4">Filter by Type</h3>
                    <div className="space-y-3">
                      <div className="flex items-center">
                        <Checkbox
                          id="idea_pool"
                          checked={filterTypes.idea_pool}
                          onCheckedChange={(checked) => setFilterTypes({ ...filterTypes, idea_pool: checked })}
                        />
                        <label htmlFor="idea_pool" className="ml-2 text-sm cursor-pointer flex items-center">
                          <Lightbulb className="h-4 w-4 mr-2 text-purple-600" />
                          Idea Pool
                        </label>
                      </div>
                      <div className="flex items-center">
                        <Checkbox
                          id="speed_quiz"
                          checked={filterTypes.speed_run_quiz}
                          onCheckedChange={(checked) => setFilterTypes({ ...filterTypes, speed_run_quiz: checked })}
                        />
                        <label htmlFor="speed_quiz" className="ml-2 text-sm cursor-pointer flex items-center">
                          <Zap className="h-4 w-4 mr-2 text-orange-600" />
                          Speed Quiz
                        </label>
                      </div>
                      <div className="flex items-center">
                        <Checkbox
                          id="questionnaire"
                          checked={filterTypes.questionnaire}
                          onCheckedChange={(checked) => setFilterTypes({ ...filterTypes, questionnaire: checked })}
                        />
                        <label htmlFor="questionnaire" className="ml-2 text-sm cursor-pointer flex items-center">
                          <ClipboardList className="h-4 w-4 mr-2 text-blue-600" />
                          Questionnaire
                        </label>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Main Content Area */}
              <div className="flex-1">
                {/* Tabs */}
                <div className="flex gap-6 mb-6 border-b">
                  <button
                    onClick={() => handleTabChange('newest')}
                    className={`pb-3 px-1 font-medium text-sm transition-colors flex items-center gap-2 ${
                      activeTab === 'newest'
                        ? 'text-purple-600 border-b-2 border-purple-600'
                        : 'text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    <Calendar className="h-4 w-4" />
                    Newest
                  </button>
                  <button
                    onClick={() => handleTabChange('top')}
                    className={`pb-3 px-1 font-medium text-sm transition-colors flex items-center gap-2 ${
                      activeTab === 'top'
                        ? 'text-purple-600 border-b-2 border-purple-600'
                        : 'text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    <TrendingUp className="h-4 w-4" />
                    Top Rated
                  </button>
                </div>

                {/* Sessions Grid */}
                {loading ? (
                  <div className="text-center py-12">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto"></div>
                    <p className="mt-4 text-gray-600">Loading sessions...</p>
                  </div>
                ) : filteredSessions.length === 0 ? (
                  <div className="text-center py-12">
                    <Globe className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                    <h3 className="text-lg font-medium text-gray-900 mb-2">No sessions found</h3>
                    <p className="text-gray-600">Try adjusting your filters or search query</p>
                  </div>
                ) : (
                  <>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      {filteredSessions.map((session) => (
                        <Card
                          key={session.id}
                          className="cursor-pointer hover:shadow-lg transition-shadow overflow-hidden"
                          onClick={() => handleSessionClick(session)}
                        >
                          {/* Colored Top Border */}
                          <div className={`h-2 bg-gradient-to-r ${getSessionTypeColor(session.session_type)}`} />
                          
                          <CardContent className="p-6">
                            {/* Header */}
                            <div className="flex items-start justify-between mb-4">
                              <div className="flex items-center gap-2">
                                <div className={`p-2 rounded-lg bg-gradient-to-r ${getSessionTypeColor(session.session_type)} text-white`}>
                                  {getSessionTypeIcon(session.session_type)}
                                </div>
                                <Badge variant="secondary" className="text-xs">
                                  {getSessionTypeName(session.session_type)}
                                </Badge>
                              </div>
                              <div className="text-2xl">
                                {getCountryFlag(session.country_code)}
                              </div>
                            </div>

                            {/* Title */}
                            <h3 className="text-xl font-semibold text-gray-900 mb-3 line-clamp-2">
                              {session.topic}
                            </h3>

                            {/* Hashtags */}
                            {session.hashtags && session.hashtags.length > 0 && (
                              <div className="flex flex-wrap gap-2 mb-4">
                                {session.hashtags.slice(0, 3).map((tag, index) => (
                                  <span key={index} className="text-xs text-purple-600 bg-purple-50 px-2 py-1 rounded">
                                    #{tag}
                                  </span>
                                ))}
                              </div>
                            )}

                            {/* Footer */}
                            <div className="flex items-center justify-between text-sm text-gray-600">
                              <div className="flex items-center gap-4">
                                <div className="flex items-center gap-1">
                                  <Clock className="h-4 w-4" />
                                  <span>{new Date(session.created_at).toLocaleDateString()}</span>
                                </div>
                                <div className="flex items-center gap-1">
                                  <Users className="h-4 w-4" />
                                  <span>{session.participant_count || 0} joined</span>
                                </div>
                              </div>
                              {session.participant_count > 10 && (
                                <Badge variant="destructive" className="bg-red-500">Hot</Badge>
                              )}
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                    </div>

                    {/* Loading More Indicator */}
                    {loadingMore && (
                      <div className="text-center py-8">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600 mx-auto"></div>
                        <p className="mt-2 text-sm text-gray-600">Loading more sessions...</p>
                      </div>
                    )}

                    {/* End of Results */}
                    {!hasMore && sessions.length > 0 && (
                      <div className="text-center py-8">
                        <p className="text-gray-500">You've reached the end of the feed</p>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>
        </>
      ) : (
        <>
          {/* Mobile Layout */}
          {/* Mobile Top Header */}
          <div className="bg-white border-b sticky top-0 z-50">
            <div className="flex items-center justify-between px-4 h-14">
              <div className="flex items-center gap-2">
                <img alt="Pollar Logo" className="w-6 h-6" src="/assets/logo/pollar.svg" />
                <h1 className="text-xl font-bold text-purple-600">Pollar</h1>
              </div>
              <div className="flex items-center gap-3">
                <button onClick={() => setShowMobileSearch(!showMobileSearch)}>
                  <Search className="h-5 w-5 text-gray-600" />
                </button>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button>
                      <UserCircle className="h-6 w-6 text-gray-600" />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-48">
                    <DropdownMenuItem onClick={() => navigate('/profile')}>
                      <UserCircle className="h-4 w-4 mr-2" />
                      Profile
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={handleLogout}>
                      <LogOut className="h-4 w-4 mr-2" />
                      Logout
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </div>

            {/* Mobile Search Bar (Expandable) */}
            {showMobileSearch && (
              <div className="px-4 pb-3">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <Input
                    type="text"
                    placeholder="Search..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-10 w-full bg-gray-100 border-0"
                  />
                </div>
              </div>
            )}

            {/* Mobile Tabs - Horizontal Scroll */}
            <div className="overflow-x-auto scrollbar-hide border-b">
              <div className="flex gap-2 px-4 py-2 min-w-max">
                <Button
                  size="sm"
                  variant={activeTab === 'newest' ? 'default' : 'outline'}
                  onClick={() => handleTabChange('newest')}
                  className={activeTab === 'newest' ? 'bg-purple-600' : ''}
                >
                  <Calendar className="h-3 w-3 mr-1" />
                  Newest
                </Button>
                <Button
                  size="sm"
                  variant={activeTab === 'top' ? 'default' : 'outline'}
                  onClick={() => handleTabChange('top')}
                  className={activeTab === 'top' ? 'bg-purple-600' : ''}
                >
                  <TrendingUp className="h-3 w-3 mr-1" />
                  Top
                </Button>
                <Button
                  size="sm"
                  variant={filterTypes.idea_pool ? 'default' : 'outline'}
                  onClick={() => setFilterTypes({ ...filterTypes, idea_pool: !filterTypes.idea_pool })}
                  className={filterTypes.idea_pool ? 'bg-purple-500' : ''}
                >
                  <Lightbulb className="h-3 w-3 mr-1" />
                  Idea Pool
                </Button>
                <Button
                  size="sm"
                  variant={filterTypes.speed_run_quiz ? 'default' : 'outline'}
                  onClick={() => setFilterTypes({ ...filterTypes, speed_run_quiz: !filterTypes.speed_run_quiz })}
                  className={filterTypes.speed_run_quiz ? 'bg-orange-500' : ''}
                >
                  <Zap className="h-3 w-3 mr-1" />
                  Speed Quiz
                </Button>
                <Button
                  size="sm"
                  variant={filterTypes.questionnaire ? 'default' : 'outline'}
                  onClick={() => setFilterTypes({ ...filterTypes, questionnaire: !filterTypes.questionnaire })}
                  className={filterTypes.questionnaire ? 'bg-blue-500' : ''}
                >
                  <ClipboardList className="h-3 w-3 mr-1" />
                  Questionnaire
                </Button>
              </div>
            </div>
          </div>

          {/* Mobile Content */}
          <div className="pb-20 px-4 pt-4">
            {loading ? (
              <div className="text-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto"></div>
                <p className="mt-4 text-gray-600">Loading sessions...</p>
              </div>
            ) : filteredSessions.length === 0 ? (
              <div className="text-center py-12">
                <Globe className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">No sessions found</h3>
                <p className="text-gray-600">Try adjusting your filters</p>
              </div>
            ) : (
              <>
                <div className="space-y-4">
                  {filteredSessions.map((session) => (
                    <Card
                      key={session.id}
                      className="cursor-pointer active:scale-98 transition-transform overflow-hidden"
                      onClick={() => handleSessionClick(session)}
                    >
                      <div className={`h-1 bg-gradient-to-r ${getSessionTypeColor(session.session_type)}`} />
                      <CardContent className="p-4">
                        <div className="flex items-start justify-between mb-3">
                          <div className="flex items-center gap-2">
                            <div className={`p-1.5 rounded-lg bg-gradient-to-r ${getSessionTypeColor(session.session_type)} text-white`}>
                              {getSessionTypeIcon(session.session_type)}
                            </div>
                            <Badge variant="secondary" className="text-xs">
                              {getSessionTypeName(session.session_type)}
                            </Badge>
                          </div>
                          <div className="text-xl">
                            {getCountryFlag(session.country_code)}
                          </div>
                        </div>

                        <h3 className="text-base font-semibold text-gray-900 mb-2 line-clamp-2">
                          {session.topic}
                        </h3>

                        {session.hashtags && session.hashtags.length > 0 && (
                          <div className="flex flex-wrap gap-1 mb-3">
                            {session.hashtags.slice(0, 2).map((tag, index) => (
                              <span key={index} className="text-xs text-purple-600 bg-purple-50 px-2 py-0.5 rounded">
                                #{tag}
                              </span>
                            ))}
                          </div>
                        )}

                        <div className="flex items-center justify-between text-xs text-gray-600">
                          <div className="flex items-center gap-3">
                            <div className="flex items-center gap-1">
                              <Users className="h-3 w-3" />
                              <span>{session.participant_count || 0}</span>
                            </div>
                            <div className="flex items-center gap-1">
                              <Clock className="h-3 w-3" />
                              <span>{new Date(session.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</span>
                            </div>
                          </div>
                          {session.participant_count > 10 && (
                            <Badge variant="destructive" className="bg-red-500 text-xs">Hot</Badge>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>

                {loadingMore && (
                  <div className="text-center py-6">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-purple-600 mx-auto"></div>
                  </div>
                )}

                {!hasMore && sessions.length > 0 && (
                  <div className="text-center py-6">
                    <p className="text-sm text-gray-500">No more sessions</p>
                  </div>
                )}
              </>
            )}
          </div>

          {/* Mobile Bottom Navigation */}
          <div className="fixed bottom-0 left-0 right-0 bg-white border-t z-50">
            <div className="flex items-center justify-around py-2">
              <button 
                onClick={() => navigate('/dashboard')}
                className="flex flex-col items-center p-2 text-gray-500"
              >
                <Layers className="h-5 w-5" />
                <span className="text-xs mt-1">My Sessions</span>
              </button>
              <button 
                onClick={() => navigate('/session/setup')}
                className="flex flex-col items-center -mt-6"
              >
                <div className="bg-gradient-to-r from-purple-600 to-indigo-600 rounded-full p-4 shadow-lg">
                  <Plus className="h-6 w-6 text-white" />
                </div>
              </button>
              <button 
                onClick={() => navigate('/social')}
                className="flex flex-col items-center p-2 text-purple-600"
              >
                <Globe className="h-5 w-5" />
                <span className="text-xs mt-1">Explore</span>
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default Social;
